import { useContext } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useAPI } from '../lib/hooks';
import useRestService from './RestService';
import { AppContext } from '../sporttia/AppContext';
import constants from '../config/constants';
import {
    GetReports200Response,
    GetReportsQueryParams,
} from '../types/api/paths/Report';

const resource = `${constants.boundedContextPrefixes.MANAGER}/reports`;

/**
 * Hook to access all 'reports' resources.
 */
export default function useReportsService() {
    const { migrationApis } = useContext(AppContext)!;
    const [request] = useAPI(migrationApis);
    const { get } = useRestService(resource);

    /**
     * GET /reports/:id/rows request.
     * @param id Report ID.
     * @param params Resource parameters to be sent with the request.
     * @returns {Promise<unknown>|*} Return request promise.
     */
    function getReportsRows(
        id: any,
        params: Record<string, unknown> | undefined,
    ) {
        return request('GET', `${resource}/${id}/rows`, params);
    }

    /**
     * GET /reports/:id/columns request.
     * @param id Report ID.
     * @param params Resource parameters to be sent with the request.
     * @returns {Promise<unknown>|*} Return request promise.
     */
    function getReportsColumns(
        id: any,
        params: Record<string, unknown> | undefined,
    ) {
        return request('GET', `${resource}/${id}/columns`, params);
    }

    /**
     * GET /reports/:id/filters request.
     * @param id Report ID.
     * @param params Resource parameters to be sent with the request.
     * @returns {Promise<unknown>|*} Return request promise.
     */
    function getReportsFilters(
        id: any,
        params: Record<string, unknown> | undefined,
    ) {
        return request('GET', `${resource}/${id}/filters`, params);
    }

    /**
     * GET /reports request.
     */
    function useGetReports<TData = GetReports200Response>(
        params?: GetReportsQueryParams,
        queryOptions?: UseQueryOptions<GetReports200Response, unknown, TData>,
    ) {
        const queryKey = queryOptions?.queryKey ?? [
            `${resource}`,
            ...(params ? [params] : []),
        ];

        const queryFn = () =>
            request<GetReports200Response>('GET', `${resource}`, params);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    return {
        get,
        getReportsRows,
        getReportsColumns,
        getReportsFilters,
        useGetReports,
    };
}
